import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import { Container, DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
} from '@/partials/toolbar';
import { RegionFormModal } from './RegionFormModal';
import clsx from 'clsx';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  useGetRegionsQuery,
  useCreateRegionMutation,
  useUpdateRegionMutation,
  useDeleteRegionMutation,
  useGetRegionByIdQuery,
} from '@/services/RegionManagementService';
import {
  RegionResponse,
  CreateRegionPayload,
  UpdateRegionPayload,
  Operator,
} from '@/types/regions.types';
import { PaginatedApiResponse } from '@/types/api.types';
import { HarborResponse } from '@/types/harbor-management.types';
import { usePermissions } from '@/hooks/usePermissions';

interface IRegionData {
  id?: string;
  name?: string;
  status?: 'Active' | 'Inactive';
  operator?: Operator;
  harborIds?: string[];
  harbors?: HarborResponse[];
  dmo?: Operator;
}

const statusToString = (status: number) =>
  status === 1 ? 'Active' : 'Inactive';
const stringToStatus = (status: string) => (status === 'Active' ? 1 : 0);

const RegionListings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<'view' | 'edit' | 'create'>('create');
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<IRegionData>>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [confirmDialogState, setConfirmDialogState] = useState<{
    isOpen: boolean;
    regionId: string | null;
    currentStatus: string | null;
  }>({ isOpen: false, regionId: null, currentStatus: null });
  const [deleteDialogState, setDeleteDialogState] = useState<{
    isOpen: boolean;
    region: RegionResponse | null;
  }>({ isOpen: false, region: null });

  const {
    pagination,
    setPagination,
    handleSearch,
    searchTerm,
    resetSearch,
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
  } = usePagination();

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      status: statusFilter === 'all' ? undefined : stringToStatus(statusFilter),
      search: debouncedValue || undefined,
    }),
    [pagination.pageIndex, pagination.pageSize, statusFilter, debouncedValue]
  );

  const {
    data: regionsData,
    isLoading,
    isFetching,
    isError,
    refetch: refetchRegions,
  } = useGetRegionsQuery(queryParams, { refetchOnMountOrArgChange: true }) as {
    data: PaginatedApiResponse<RegionResponse[]> | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  };

  const [createRegion, { isLoading: isCreating }] = useCreateRegionMutation();
  const [updateRegion, { isLoading: isUpdating }] = useUpdateRegionMutation();
  const [deleteRegion, { isLoading: isDeleting }] = useDeleteRegionMutation();
  const { data: regionDetail, isLoading: isRegionDetailLoading } =
    useGetRegionByIdQuery(selectedRegionId!, {
      skip: !selectedRegionId || mode === 'create',
    });
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (regionDetail && (mode === 'edit' || mode === 'view')) {
      setFormData({
        id: regionDetail.id,
        name: regionDetail.name,
        status: statusToString(regionDetail.status) as 'Active' | 'Inactive',
        operator: regionDetail.region_operator
          ? {
              id: regionDetail.region_operator.id,
              name: regionDetail.region_operator.name,
              email: regionDetail.region_operator.email,
            }
          : undefined,
        harborIds: regionDetail.harbors?.map((harbor) => harbor.id) || [],
        harbors: regionDetail.harbors || [],
        dmo: regionDetail.dmo
          ? {
              id: regionDetail.dmo.id,
              name: regionDetail.dmo.name,
              email: regionDetail.dmo.email,
            }
          : undefined,
      });
    }
  }, [regionDetail, mode]);

  const filteredData = useMemo(() => {
    if (!regionsData?.data) return [];
    return regionsData.data.map((region: RegionResponse) => ({
      ...region,
      status: statusToString(region.status),
    }));
  }, [regionsData]);

  // --- Pagination Fix: update totalPages and totalItems just like HarborListings ---
  useEffect(() => {
    if (regionsData) {
      handleTotalPages(regionsData.totalPages || 1);
      handleTotalItems(regionsData.totalItems || 0);
    }
  }, [regionsData, handleTotalPages, handleTotalItems]);

  const handleOpenModal = useCallback(
    (mode: 'view' | 'edit' | 'create', region?: RegionResponse) => {
      setMode(mode);
      setErrors({});
      if (mode === 'create') {
        setFormData({});
        setSelectedRegionId(null);
      } else if (region) {
        setSelectedRegionId(region.id);
      }
      setIsModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRegionId(null);
    setFormData({});
    setErrors({});
    refetchRegions();
  }, [refetchRegions]);

  const handleSubmit = useCallback(
    async (harborIds?: string[]) => {
      try {
        if (!formData.name || !formData.status || !formData.operator?.id) {
          throw new Error('Missing required fields');
        }
        const payload: CreateRegionPayload | UpdateRegionPayload = {
          name: formData.name,
          status: stringToStatus(formData.status as string),
          region_operator_id: formData.operator.id,
          ...(harborIds && harborIds.length > 0 && { harborIds }),
          dmo_id: formData.dmo?.id,
        };
        if (mode === 'create') {
          await createRegion(payload as CreateRegionPayload).unwrap();
          enqueueSnackbar('Region created successfully', {
            variant: 'success',
          });
        } else if (mode === 'edit' && selectedRegionId) {
          const originalHarborIds =
            regionDetail?.harbors?.map((h) => h.id) || [];
          const hasHarborChanges =
            JSON.stringify(originalHarborIds.sort()) !==
            JSON.stringify((harborIds || []).sort());
          await updateRegion({
            region_id: selectedRegionId,
            data: {
              name: formData.name,
              status: stringToStatus(formData.status as string),
              region_operator_id: formData.operator.id,
              ...(hasHarborChanges && harborIds && { harborIds }),
              dmo_id: formData.dmo?.id,
            },
          }).unwrap();
          enqueueSnackbar('Region updated successfully', {
            variant: 'success',
          });
        }
        setIsModalOpen(false);
        refetchRegions();
      } catch (error) {
        let message = 'An error occurred';
        if (error && typeof error === 'object') {
          // API error shape: { data: { message: string } }
          if (
            'data' in error &&
            error.data &&
            typeof error.data === 'object' &&
            'message' in error.data &&
            typeof (error.data as { message?: unknown }).message === 'string'
          ) {
            message = (error.data as { message: string }).message;
          } else if (
            'message' in error &&
            typeof (error as { message?: unknown }).message === 'string'
          ) {
            message = (error as { message: string }).message;
          }
        }
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    },
    [
      mode,
      formData,
      selectedRegionId,
      createRegion,
      updateRegion,
      enqueueSnackbar,
      refetchRegions,
      regionDetail,
    ]
  );

  const handleDelete = useCallback((region: RegionResponse) => {
    setDeleteDialogState({ isOpen: true, region });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialogState.region) return;
    try {
      await deleteRegion(deleteDialogState.region.id).unwrap();
      enqueueSnackbar('Region deleted successfully', { variant: 'success' });
      refetchRegions();
    } catch {
      enqueueSnackbar('Failed to delete region', { variant: 'error' });
    }
    setDeleteDialogState({ isOpen: false, region: null });
  }, [deleteDialogState, deleteRegion, enqueueSnackbar, refetchRegions]);

  const handleStatusToggle = useCallback(
    (regionId: string, currentStatus: string) => {
      setConfirmDialogState({ isOpen: true, regionId, currentStatus });
    },
    []
  );

  const handleConfirmStatusToggle = useCallback(async () => {
    if (!confirmDialogState.regionId || !confirmDialogState.currentStatus)
      return;
    try {
      const newStatus = confirmDialogState.currentStatus === 'Active' ? 0 : 1;
      await updateRegion({
        region_id: confirmDialogState.regionId,
        data: { status: newStatus },
      }).unwrap();
      enqueueSnackbar(
        `Region status updated to ${newStatus === 1 ? 'Active' : 'Inactive'}`,
        { variant: 'success' }
      );
      refetchRegions();
    } catch {
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    }
    setConfirmDialogState({
      isOpen: false,
      regionId: null,
      currentStatus: null,
    });
  }, [confirmDialogState, updateRegion, enqueueSnackbar, refetchRegions]);

  const handleFilterChange = useCallback(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [setPagination]);

  const columns: ColumnDef<RegionResponse & { status: string }>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) =>
          hasPermission('region_management_read') ? (
            <button
              onClick={() => handleOpenModal('view', row.original)}
              className="text-sm font-medium text-gray-900 hover:text-primary-active contrast:hover:text-white contrast:hover:underline"
              aria-label={`View ${row.original.name}`}
            >
              {row.original.name}
            </button>
          ) : (
            <span className="text-sm font-medium text-gray-900">
              {row.original.name}
            </span>
          ),
        meta: {
          className: 'min-w-[300px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row) =>
          row.region_operator?.name || row.region_operator?.email || 'N/A',
        id: 'operator',
        header: 'Operator',
        enableSorting: false,
        meta: {
          className: 'min-w-[180px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) =>
          hasPermission('region_management_update') ? (
            <button
              onClick={() =>
                handleStatusToggle(row.original.id, row.original.status)
              }
              className={clsx(
                'px-2 py-1 rounded',
                row.original.status === 'Active'
                  ? 'bg-green-100 dark:bg-green-900 contrast:bg-green-900 contrast:text-green-100'
                  : 'bg-red-100 dark:bg-red-900 contrast:bg-red-900 contrast:text-red-100'
              )}
              aria-label={`Toggle status for ${row.original.name} to ${row.original.status === 'Active' ? 'Inactive' : 'Active'}`}
            >
              {row.original.status}
            </button>
          ) : (
            <span
              className={clsx(
                'px-2 py-1 rounded',
                row.original.status === 'Active'
                  ? 'bg-green-100 dark:bg-green-900 contrast:bg-green-900 contrast:text-green-100'
                  : 'bg-red-100 dark:bg-red-900 contrast:bg-red-900 contrast:text-red-100'
              )}
            >
              {row.original.status}
            </span>
          ),
        meta: {
          className: 'min-w-[180px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {hasPermission('region_management_read') && (
              <button
                onClick={() => handleOpenModal('view', row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {hasPermission('region_management_update') && (
              <button
                onClick={() => handleOpenModal('edit', row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {hasPermission('region_management_delete') && (
              <button
                onClick={() => handleDelete(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-gray-500"
                />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [handleOpenModal, handleDelete, handleStatusToggle, hasPermission]
  );

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <h1
              id="region-management-heading"
              className="text-xl font-medium leading-none text-gray-900"
              tabIndex={-1}
            >
              Regions
            </h1>
            <ToolbarDescription>
              You can manage your Regions here.
            </ToolbarDescription>
          </ToolbarHeading>
          {hasPermission('region_management_create') && (
            <ToolbarActions>
              <button
                className="btn btn-sm btn-light"
                onClick={() => handleOpenModal('create')}
                aria-label="Add new region"
              >
                Add New Region
              </button>
            </ToolbarActions>
          )}
        </Toolbar>
      </Container>
      <Container>
        <div className="card card-grid min-w-full">
          <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-regions"
                aria-label="Search regions"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                />
                <input
                  placeholder="Search regions"
                  id="search-regions"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search regions"
                />
              </label>
              <select
                className="select select-sm w-36 h-8 sm:h-10 text-sm"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  handleFilterChange();
                }}
                aria-label="Filter regions by status"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            {(searchTerm || statusFilter !== 'all') && (
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                onClick={() => {
                  setStatusFilter('all');
                  resetSearch();
                  handleFilterChange();
                }}
                aria-label="Clear all filters"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filters
              </button>
            )}
          </div>
          <div className="card-body">
            {isLoading || isFetching ? (
              <div className="text-center py-4">Loading regions...</div>
            ) : isError ? (
              <div className="text-center py-4 text-red-500">
                Failed to load regions. Please try again.
              </div>
            ) : (
              <DataGrid
                columns={columns}
                data={filteredData}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems,
                  totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'name', desc: false }]}
                serverSide={true}
                onFilterReset={() => {
                  setStatusFilter('all');
                  resetSearch();
                  handleFilterChange();
                }}
                hasActiveFilters={!!searchTerm || statusFilter !== 'all'}
                key={JSON.stringify(queryParams)}
              />
            )}
          </div>
        </div>
      </Container>
      <RegionFormModal
        open={isModalOpen}
        onClose={handleCloseModal}
        mode={mode}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isCreating || isUpdating || isRegionDetailLoading}
      />
      <ConfirmationDialog
        open={confirmDialogState.isOpen}
        onClose={() =>
          setConfirmDialogState({
            isOpen: false,
            regionId: null,
            currentStatus: null,
          })
        }
        onConfirm={handleConfirmStatusToggle}
        message={`Are you sure you want to change the region status to ${confirmDialogState.currentStatus === 'Active' ? 'Inactive' : 'Active'}?`}
        cancelButtonText="No"
        confirmButtonText="Yes"
        disabled={isUpdating}
      />
      <ConfirmationDialog
        open={deleteDialogState.isOpen}
        onClose={() => setDeleteDialogState({ isOpen: false, region: null })}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete ${deleteDialogState.region?.name || 'this region'}?`}
        cancelButtonText="No"
        confirmButtonText="Yes"
        disabled={isDeleting}
      />
    </>
  );
};

export default RegionListings;
